import React, { useState } from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import {
  BarChart3, MessageSquare, FileText, Rss, LogOut,
  Recycle, Menu, X, ChevronRight
} from 'lucide-react';
import { AdminProvider, useAdmin } from './AdminContext';

const NAV_ITEMS = [
  { to: '/admin', end: true, label: 'Dashboard', icon: BarChart3 },
  { to: '/admin/submissions', label: 'Submissions', icon: MessageSquare },
  { to: '/admin/posts', label: 'Blog Posts', icon: FileText },
  { to: '/admin/news', label: 'News Feed', icon: Rss },
];

function LayoutShell() {
  const { token, adminName, logout, loading } = useAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060d1b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4fd1c5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) return <Navigate to="/admin/login" replace />;

  const SidebarContent = () => (
    <>
      {/* Brand */}
      <div className="p-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#4fd1c5] flex items-center justify-center flex-shrink-0">
            <Recycle className="w-4.5 h-4.5 text-[#0a1628]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-white font-bold text-sm truncate">Grenew CRM</h1>
            <p className="text-gray-500 text-[10px] tracking-wider uppercase">Admin</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5">
        <p className="text-gray-500 text-[10px] font-semibold tracking-wider uppercase px-3 pt-2 pb-2">Menu</p>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                isActive
                  ? 'bg-[#4fd1c5]/10 text-[#4fd1c5]'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {isActive && <ChevronRight className="w-3.5 h-3.5 opacity-50" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4fd1c5] to-[#38b2ac] flex items-center justify-center text-[#0a1628] text-xs font-bold flex-shrink-0">
            {adminName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate">{adminName}</p>
            <p className="text-gray-500 text-[10px]">Administrator</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#060d1b] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 min-h-screen bg-[#0a1628]/80 border-r border-white/[0.06] sticky top-0 h-screen backdrop-blur-sm">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 h-full bg-[#0a1628] flex flex-col animate-slide-in">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 min-h-screen flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden bg-[#0a1628]/80 border-b border-white/[0.06] px-4 py-3 flex items-center justify-between sticky top-0 z-40 backdrop-blur-sm">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#4fd1c5] flex items-center justify-center">
              <Recycle className="w-3.5 h-3.5 text-[#0a1628]" />
            </div>
            <span className="text-white font-semibold text-sm">Grenew</span>
          </div>
          <button onClick={logout} className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg">
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminProvider>
      <LayoutShell />
    </AdminProvider>
  );
}
